import { useState } from "react";
import { toast } from "react-toastify";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useGenericErrorModal } from "src/components/reusable/hooks/useGenericErrorModal";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { TCartReservation, TEvent } from "src/shared/types/globalStotre.types";
import { createState } from "src/utils/create_zustand_store";
import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { getExchangeEligibleReservations } from "./utils.event-attendance";

export const useExchangeModal = createState({
  isOpen: false,
  newEvent: null as TEvent | null,
});

export function useExchangeReservation() {
  const { exchangeReservationApi, getUpcomingEventsApi } = useApiRoutes();
  const { cart, events, setGlobalState } = useGlobalStore();
  const { fireErrorModal } = useGenericErrorModal();
  const router = useRouterExt();
  const exchangeModal = useExchangeModal();

  const eligibleReservations = getExchangeEligibleReservations({
    events,
    newEvent: exchangeModal.newEvent,
  });

  const [selectedOldEventCode, setSelectedOldEventCode] = useState<string | null>(null);
  const [seatCount, setSeatCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    exchangeModal.set({ isOpen: false, newEvent: null });
    setSelectedOldEventCode(null);
    setSeatCount(1);
  };

  const refreshEvents = async (isReservationEnabled: boolean) => {
    try {
      const queryParams = isReservationEnabled ? { sortBy: "opt_ins_first" } : {};
      const response = await getUpcomingEventsApi.get({ queryParams });
      const optedInCountForEventReservation =
        response.optedInCountForEventReservation ?? null;
      setGlobalState({
        events: response.sortedEvents,
        optedInCountForEventReservation,
        reservationLimitsByPass: response.reservationLimitsByPass ?? null,
      });
    } catch {
      // silent — stale data is acceptable here
    }
  };

  const executeExchange = async (isReservationEnabled: boolean) => {
    const newEvent = exchangeModal.newEvent;
    const newEventCode = newEvent?.eventCode;
    if (!newEventCode || !selectedOldEventCode) return;

    if (newEvent?.isPaymentRequired) {
      const oldReservation = eligibleReservations.find(
        (e) => e.eventCode === selectedOldEventCode
      );
      if (!oldReservation) return;

      const reservation: TCartReservation = {
        eventCode: newEvent.eventCode,
        reservationEventCode: newEvent.reservationEventCode ?? null,
        reservationTicketTypeCode: newEvent.reservationTicketTypeCode ?? "",
        reservationPriceCode: newEvent.reservationPriceCode ?? null,
        reservationClassName: newEvent.reservationClassName ?? null,
        seatCount,
        // Seed from the new event's pricing. `price` is optimistic (manageCart
        // overwrites it with TM's real per-seat charge); serviceFee /
        // additionalFee are kept as-is, so these are the fees actually charged.
        // `?? 0` covers unpriced (null) events.
        price: newEvent.price ?? 0,
        serviceFee: newEvent.serviceFee ?? 0,
        additionalFee: newEvent.additionalFee ?? 0,
      };

      const existing = (cart?.reservations || []).filter(
        (r) => r.eventCode !== newEvent.eventCode
      );
      setGlobalState({
        cart: {
          ...cart,
          reservations: [...existing, reservation],
          isExchange: true,
          oldEventCode: selectedOldEventCode,
          oldSeatCount: Number(oldReservation.optedInSeatCount ?? 0),
        },
      });

      closeModal();
      router.pushSchema({
        pathname: routes.checkout,
        query: { tab: constants.checkoutTabs.checkout },
      });
      return;
    }

    const oldReservation = eligibleReservations.find(
      (e) => e.eventCode === selectedOldEventCode
    );
    const oldSeatCount = Number(oldReservation?.optedInSeatCount ?? 0);

    setIsSubmitting(true);
    try {
      await exchangeReservationApi.post({
        newEventCode,
        oldEventCode: selectedOldEventCode,
        seatCount,
        oldSeatCount,
      });
      closeModal();
      toast.success("Your reservation has been exchanged successfully.");
      await refreshEvents(isReservationEnabled);
    } catch (err) {
      fireErrorModal(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    eligibleReservations,
    selectedOldEventCode,
    setSelectedOldEventCode,
    seatCount,
    setSeatCount,
    isSubmitting,
    closeModal,
    executeExchange,
  };
}
